
'use client';

import React, { useState } from 'react';
import type { ColumnConfig } from '../types';

interface ScrumboardViewProps {
  data: any[];
  columns: ColumnConfig[];
  selectedColumn: string;
  onColumnChange: (columnName: string) => void;
  onCardMove?: (cardId: string, newStatus: string) => void;
}

interface DraggableCardProps {
  card: any;
  columns: ColumnConfig[];
  onDragStart: (e: React.DragEvent, card: any) => void;
}

function DraggableCard({ card, columns, onDragStart }: DraggableCardProps) {
  const primaryColumn = columns.find(col => col.columnType === 'text') || columns[0];
  const primaryValue = card[primaryColumn?.field || Object.keys(card)[0]];

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, card)}
      className="bg-white p-3 rounded-lg border border-gray-200 hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing mb-2 select-none"
    >
      <div className="font-medium text-gray-900 text-sm mb-2">{primaryValue}</div>
      <div className="space-y-1">
        {columns.slice(1, 4).map((column) => {
          if (column.field && card[column.field] !== undefined) {
            return (
              <div key={column.field} className="text-xs text-gray-600">
                <span className="font-medium">{column.columnName}:</span> {card[column.field]}
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

export const ScrumboardView: React.FC<ScrumboardViewProps> = ({ 
  data, 
  columns, 
  selectedColumn, 
  onColumnChange, 
  onCardMove 
}) => {
  const [draggedCard, setDraggedCard] = useState<any>(null);
  
  const groupableColumns = columns.filter(col => col.allowGrouping !== false && col.field);
  
  // Group data by selected column
  const groupedData = data.reduce((groups: any, item: any) => {
    const key = item[selectedColumn] || 'Unassigned';
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {});

  const handleDragStart = (e: React.DragEvent, card: any) => {
    setDraggedCard(card);
    e.dataTransfer.setData('text/plain', JSON.stringify(card));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    
    if (draggedCard) {
      const cardId = draggedCard.id || draggedCard.sno || JSON.stringify(draggedCard);
      
      // Update the card's status in the data
      if (onCardMove) {
        onCardMove(cardId, newStatus);
      }
      
      // Update local data for immediate feedback
      const cardIndex = data.findIndex(item => 
        (item.id || item.sno || JSON.stringify(item)) === cardId
      );
      
      if (cardIndex !== -1) {
        data[cardIndex][selectedColumn] = newStatus;
      }
    }
    
    setDraggedCard(null);
  };

  const handleDragEnd = () => {
    setDraggedCard(null);
  };

  return (
    <div className="p-4">
      {/* Column Selector */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <label className="text-sm font-medium text-gray-700">Group by:</label>
        <select
          value={selectedColumn}
          onChange={(e) => onColumnChange(e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
        >
          {groupableColumns.map((column) => (
            <option key={column.field} value={column.field}>
              {column.columnName}
            </option>
          ))}
        </select>
        <div className="text-sm text-gray-600">
          Drag cards between columns to update their status
        </div>
      </div>

      {/* Boards Container */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {Object.entries(groupedData).map(([status, items]) => (
          <div 
            key={status}
            className="flex-shrink-0 w-72 bg-gray-50 rounded-lg p-4"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, status)}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-900">{status}</h3>
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                {(items as any[]).length}
              </span>
            </div>
            
            <div className="min-h-[200px] space-y-2">
              {(items as any[]).map((item, index) => (
                <DraggableCard
                  key={`${status}-${index}`}
                  card={item}
                  columns={columns}
                  onDragStart={handleDragStart}
                />
              ))}
              
              {/* Drop zone indicator */}
              <div className="min-h-[50px] border-2 border-dashed border-transparent rounded-lg flex items-center justify-center text-gray-400 text-sm transition-colors hover:border-blue-300 hover:bg-blue-50">
                Drop cards here
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Drag Preview */}
      {draggedCard && (
        <div className="fixed pointer-events-none z-50 opacity-75">
          <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-lg transform rotate-2">
            <div className="font-medium text-gray-900 text-sm">
              {draggedCard[columns[0]?.field || Object.keys(draggedCard)[0]]}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .dragging {
          opacity: 0.5;
          transform: rotate(5deg);
        }
      `}</style>
    </div>
  );
};
